import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/**
 * One polite live region for the whole admin, outside the layouts that come and
 * go: screen readers hear "Opened Reservations", "12 reservations", "No
 * payouts" without the focus moving.
 */
@Injectable({ providedIn: 'root' })
export class AdminAnnouncerService {
  private readonly document = inject(DOCUMENT);
  private region?: HTMLElement;
  private pending?: ReturnType<typeof setTimeout>;

  announce(text: string): void {
    const region = this.ensureRegion();
    clearTimeout(this.pending);
    if (region.textContent !== text) {
      region.textContent = text;
      return;
    }
    // The same words again (e.g. the same count after another filter): clear, then set on the
    // next tick, or screen readers wouldn't read it a second time.
    region.textContent = '';
    this.pending = setTimeout(() => (region.textContent = text), 50);
  }

  private ensureRegion(): HTMLElement {
    if (!this.region || !this.region.isConnected) {
      this.region = this.document.createElement('p');
      this.region.className = 'admin-visually-hidden';
      this.region.setAttribute('aria-live', 'polite');
      this.region.setAttribute('data-admin-announcer', '');
      this.document.body.appendChild(this.region);
    }
    return this.region;
  }
}
